import React from 'react';
import { Download, Copy } from 'lucide-react';
import { TickerAnalysis } from '../types';

interface JsonExportProps {
  analysis: TickerAnalysis;
}

export const JsonExport: React.FC<JsonExportProps> = ({ analysis }) => {
  
  // Safe helper to handle the "Number vs Object" conflict
  const getVal = (v: number | { price: number } | undefined): string => {
    if (v === undefined) return '0.00';
    if (typeof v === 'number') return v.toFixed(2);
    return v.price ? v.price.toFixed(2) : '0.00';
  };

  const handleDownload = () => {
    const jsonString = JSON.stringify(analysis, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${analysis.ticker}_analysis_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyMarkdown = () => {
    const md = generateMarkdown(analysis, getVal);
    navigator.clipboard.writeText(md);
    alert('Analysis copied to clipboard!');
  };

  return (
    <div className="flex gap-4 justify-center mt-8">
      <button 
        onClick={handleCopyMarkdown}
        className="flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm font-bold transition-colors border border-slate-700 shadow-lg"
      >
        <Copy size={16} /> Copy Report
      </button>
      <button 
        onClick={handleDownload}
        className="flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-cyan-400 rounded-lg text-sm font-bold transition-colors border border-slate-700 shadow-lg"
      >
        <Download size={16} /> Export JSON
      </button>
    </div>
  );
};

// Helper to generate the text report
function generateMarkdown(
  analysis: TickerAnalysis, 
  getVal: (v: number | { price: number } | undefined) => string
): string {
  const { ticker, currentPrice, scores, bestStrategy, tradePlan, riskFlags } = analysis;
  
  if (!bestStrategy) return `Analysis for ${ticker}: No suitable strategy found.`;

  // Safely extract nested optional values
  const stopLoss = getVal(tradePlan?.stopLoss);
  const takeProfit = getVal(tradePlan?.takeProfit);
  const entry = tradePlan?.entryPrice ? tradePlan.entryPrice.toFixed(2) : currentPrice.toFixed(2);
  const contracts = tradePlan?.contracts || 0;
  const capital = tradePlan?.capitalRequired?.toFixed(0) || '0';

  return `
# 🚀 ${ticker} TRADE ANALYSIS
**Price:** $${currentPrice.toFixed(2)}
**Score:** ${scores.normalizedTotal}/100 (${analysis.status})

## 🎯 RECOMMENDED STRATEGY
**${bestStrategy.name}** (${bestStrategy.direction})
* **Max Profit:** $${bestStrategy.maxProfit.toFixed(0)}
* **Max Loss:** $${bestStrategy.maxLoss.toFixed(0)}
* **Risk/Reward:** 1:${bestStrategy.riskRewardRatio.toFixed(1)}
* **Prob. Profit:** ${(bestStrategy.probabilityOfProfit * 100).toFixed(1)}%

## 📊 EXECUTION PLAN
* **Entry:** $${entry}
* **Stop Loss:** $${stopLoss}
* **Take Profit:** $${takeProfit}
* **Contracts:** ${contracts}
* **Capital Required:** $${capital}

## ⚠️ RISK CHECK
* **Earnings:** ${riskFlags.earningsInDays !== null ? riskFlags.earningsInDays + ' days' : 'N/A'}
* **Liquidity:** Grade ${riskFlags.liquidityGrade}
* **IV Rank:** ${riskFlags.ivRank.toFixed(0)}%
* **VIX Level:** ${riskFlags.vixLevel || 'NORMAL'}

---
*Generated by Alpha Options v3.0*
  `.trim();
}
